import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Tab from '@material-ui/core/Tab';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';

import { getForms } from '../../actions/publishActions';
import FormPanel from './FormPanel';
import styles from './FormsAndRegistrationScreen.module.scss';

const LINK_TYPE_STANDARD = 'Form';
const LINK_TYPE_MEET = 'Form-meet';
const STANDARD_TYPE = 'Standard';
const MEET_TYPE = 'Meet';

interface FormsAndRegistrationProps {
	mediaId: string;
	mediaDetails: any;
	links: { type: string; id: string }[];
}

const FormsAndRegistrationScreen = ({ mediaId, mediaDetails, links }: FormsAndRegistrationProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch() as any;
	const { defaultAccountId } = useSelector((state: any) => state.session);

	const [activeTab, setActiveTab] = useState<string>(STANDARD_TYPE);
	const [formTemplates, setFormTemplates] = useState<any[]>([]);

	const linkedStandardFormId = useMemo(() => {
		if (!links || !formTemplates.length) { return ''; }
		const linkedIds = links.filter((l) => l.type === LINK_TYPE_STANDARD).map((l) => l.id);
		return formTemplates.find((f) => linkedIds.includes(f.id) && f.type !== MEET_TYPE)?.id || '';
	}, [links, formTemplates]);

	const linkedMeetFormId = useMemo(() => {
		if (!links) { return ''; }
		// New format: dedicated 'Form-meet' link type
		const meetLink = links.find((l) => l.type === LINK_TYPE_MEET);
		if (meetLink) { return meetLink.id; }
		// Legacy format: 'Form' link pointing to a Meet-type template
		if (!formTemplates.length) { return ''; }
		const linkedIds = links.filter((l) => l.type === LINK_TYPE_STANDARD).map((l) => l.id);
		return formTemplates.find((f) => linkedIds.includes(f.id) && f.type === MEET_TYPE)?.id || '';
	}, [links, formTemplates]);

	useEffect(() => {
		dispatch(getForms(defaultAccountId)).then((respData: any) => {
			if (Array.isArray(respData)) {
				setFormTemplates(respData);
			}
		});
	}, [defaultAccountId, dispatch]);

	return (
		<div className={styles.formsAndRegistration}>
			<TabContext value={activeTab}>
				<TabList onChange={(_, v: string) => setActiveTab(v)} indicatorColor="primary" textColor="primary">
					<Tab label={t('FORMS_AND_REGISTRATIONS_STANDARD_TAB')} value={STANDARD_TYPE} />
					<Tab label={t('FORMS_AND_REGISTRATIONS_MEET_TAB')} value={MEET_TYPE} />
				</TabList>
				<div style={{ display: activeTab === STANDARD_TYPE ? 'block' : 'none' }}>
					<FormPanel
						formType={STANDARD_TYPE}
						mediaId={mediaId}
						mediaDetails={mediaDetails}
						formTemplates={formTemplates}
						linkedFormId={linkedStandardFormId}
						links={links || []}
						onFormTemplatesChange={setFormTemplates}
					/>
				</div>
				<div style={{ display: activeTab === MEET_TYPE ? 'block' : 'none' }}>
					<FormPanel
						formType={MEET_TYPE}
						mediaId={mediaId}
						mediaDetails={mediaDetails}
						formTemplates={formTemplates}
						linkedFormId={linkedMeetFormId}
						links={links || []}
						onFormTemplatesChange={setFormTemplates}
					/>
				</div>
			</TabContext>
		</div>
	);
};

export default FormsAndRegistrationScreen;
